import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Search, Send, X } from 'lucide-react';
import { ROLE_LABELS, type ClubPlayer, type ClubSeason, type ClubTeamDetail, type ClubTeamStaff, type PlayhqSeason, type PlayhqTeam } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { formatLongDay } from '../lib/dates';
import { timeAgo } from '../lib/format';
import { Alert, Button, Dialog, Field, LoadingBlock, Section } from '../components/ui';
import { ClubPlayerDialog, GoLiveDialog, TeamStatusBadge, useClub } from '../components/clubs/ClubParts';

type StaffRole = ClubTeamStaff['role'];
const STAFF_STATUS: Record<ClubTeamStaff['status'], { label: string; cls: string }> = {
  member: { label: 'On the team', cls: 'text-ok' },
  invited: { label: 'Invited', cls: 'text-action' },
  planned: { label: 'Not sent yet', cls: 'text-muted' },
};
const CONTACT_STATUS: Record<string, { label: string; cls: string }> = {
  joined: { label: 'on the team', cls: 'text-ok' },
  invited: { label: 'invited', cls: 'text-action' },
  held: { label: 'not sent yet', cls: 'text-muted' },
  expired: { label: 'invite expired', cls: 'text-warn' },
  not_sent: { label: 'not sent', cls: 'text-warn' },
};

export function ClubTeamPage() {
  const { clubId = '', clubTeamId = '' } = useParams();
  const club = useClub(clubId);
  const key = ['club-team', clubId, clubTeamId];
  const team = useQuery({ queryKey: key, queryFn: () => api.get<ClubTeamDetail>(`/clubs/${clubId}/teams/${clubTeamId}`) });
  const [dialog, setDialog] = useState<'live' | 'players' | 'edit' | 'remove' | null>(null);

  if (team.isLoading || club.isLoading) return <LoadingBlock />;
  if (team.isError || !team.data || !club.data) {
    return (
      <div className="space-y-4">
        <Alert>{errorMessage(team.error ?? club.error)}</Alert>
        <Link to={`/clubs/${clubId}`} className="btn-secondary">
          Back to the club
        </Link>
      </div>
    );
  }
  const t = team.data;
  const c = club.data;
  const admin = c.permissions.includes('club:manage');
  const waiting = t.staff.filter((s) => s.status === 'planned').length + t.roster.flatMap((p) => p.contacts).filter((x) => x.status === 'held').length;

  return (
    <>
      <Link to={`/clubs/${clubId}`} className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> {c.name}
      </Link>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-[24px] leading-tight sm:text-[28px]">{t.name}</h1>
            <TeamStatusBadge team={t} />
          </div>
          <p className="mt-1 text-muted">{[t.ageGroup, t.seasonName, t.playhq ? `PlayHQ: ${t.playhq.gradeName ?? 'linked'}` : null].filter(Boolean).join(' · ') || 'No season'}</p>
        </div>
        {admin && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setDialog('edit')}>
              Edit
            </Button>
            <Button variant="secondary" onClick={() => setDialog('remove')}>
              {t.status === 'live' ? 'Remove from club' : 'Delete draft'}
            </Button>
          </div>
        )}
      </header>

      {/* Where the team is up to, and the one big action. */}
      <div className={`mb-6 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border p-4 ${t.status === 'live' ? 'border-line bg-surface' : 'border-warn/40 bg-warn-soft'}`}>
        <div className="min-w-[240px] flex-1 text-[14px]">
          {t.status === 'draft' && (
            <>
              <p className="font-semibold">Draft: nobody has been emailed</p>
              <p className="mt-0.5">Add the coaches and players, then send the invites now or on a date. {waiting > 0 && `${waiting} ${waiting === 1 ? 'invite is' : 'invites are'} ready to go.`}</p>
            </>
          )}
          {t.status === 'scheduled' && (
            <>
              <p className="font-semibold">Invites go out on {formatLongDay(t.goLiveOn!)}</p>
              <p className="mt-0.5">{waiting} {waiting === 1 ? 'invite is' : 'invites are'} waiting. Anyone you add before then is included.</p>
            </>
          )}
          {t.status === 'live' && (
            <>
              <p className="font-semibold">Live</p>
              <p className="mt-0.5 text-muted">Coaches and players you add now are invited straight away. The team’s coaches run its sessions; the club can’t see them.</p>
            </>
          )}
        </div>
        {t.status !== 'live' && (
          <Button onClick={() => setDialog('live')}>
            <Send className="size-4" aria-hidden /> {t.status === 'scheduled' ? 'Change when' : 'Save and send invites…'}
          </Button>
        )}
        {t.status === 'live' && waiting > 0 && (
          <Button variant="secondary" onClick={() => setDialog('live')}>
            Send {waiting} waiting {waiting === 1 ? 'invite' : 'invites'}…
          </Button>
        )}
      </div>

      <Staff clubId={clubId} team={t} />
      <Players clubId={clubId} team={t} onAdd={() => setDialog('players')} />
      {admin && c.playhq.connected && <PlayhqLink clubId={clubId} team={t} />}

      {dialog === 'live' && <GoLiveDialog clubId={clubId} team={t} onClose={() => setDialog(null)} />}
      {dialog === 'players' && <AddPlayersDialog clubId={clubId} team={t} onClose={() => setDialog(null)} />}
      {dialog === 'edit' && <EditTeamDialog clubId={clubId} team={t} onClose={() => setDialog(null)} />}
      {dialog === 'remove' && <RemoveTeamDialog clubId={clubId} team={t} onClose={() => setDialog(null)} />}
    </>
  );
}

// ---------- Coaches ----------

function Staff({ clubId, team }: { clubId: string; team: ClubTeamDetail }) {
  const qc = useQueryClient();
  const key = ['club-team', clubId, team.id];
  const [email, setEmail] = useState('');
  const hasHead = team.staff.some((s) => s.role === 'head_coach');
  const [role, setRole] = useState<StaffRole>(hasHead ? 'assistant_coach' : 'head_coach');
  const add = useMutation({
    mutationFn: () => api.post<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/staff`, { email, role }),
    onSuccess: (t) => {
      qc.setQueryData(key, t);
      setEmail('');
      setRole('assistant_coach');
    },
  });
  const remove = useMutation({
    mutationFn: (s: ClubTeamStaff) => (s.inviteId ? api.del<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/staff/invites/${s.inviteId}`) : api.del<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/staff/members/${s.userId}`)),
    onSuccess: (t) => qc.setQueryData(key, t),
  });
  const f = fieldErrors(add.error);
  return (
    <Section title="Coaches" description={hasHead ? undefined : 'Every team needs a head coach: they run its sessions on Hoopify.'}>
      {team.staff.length > 0 && (
        <ul className="mb-4 divide-y divide-line rounded-lg border border-line bg-surface">
          {team.staff.map((s) => (
            <li key={s.userId ?? s.inviteId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{s.name ?? s.email}</p>
                <p className="text-[13px] text-muted">
                  {ROLE_LABELS[s.role]}
                  {s.name ? ` · ${s.email}` : ''}
                </p>
              </div>
              <span className={`text-[13px] font-medium ${STAFF_STATUS[s.status].cls}`}>{STAFF_STATUS[s.status].label}</span>
              <button className="btn-ghost p-1.5" onClick={() => remove.mutate(s)} aria-label={`Remove ${s.name ?? s.email}`} title={s.status === 'member' ? 'Take off the team' : 'Cancel invite'}>
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {remove.isError && (
        <div className="mb-3">
          <Alert>{errorMessage(remove.error)}</Alert>
        </div>
      )}
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); add.mutate(); }} className="grid max-w-[640px] gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end" noValidate>
        <Field label="Add a coach by email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={f.email} placeholder="coach@example.com" />
        <div className="space-y-1.5">
          <label htmlFor="staff-role" className="label-caps block">
            As
          </label>
          <select id="staff-role" className="field-input" value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
            <option value="head_coach">{ROLE_LABELS.head_coach}</option>
            <option value="assistant_coach">{ROLE_LABELS.assistant_coach}</option>
            <option value="viewer">{ROLE_LABELS.viewer} (e.g. team manager)</option>
          </select>
        </div>
        <Button type="submit" loading={add.isPending} disabled={!email.trim()} className={f.email ? 'sm:mb-6' : ''}>
          <Plus className="size-4" aria-hidden /> Add
        </Button>
      </form>
      <p className="mt-2 text-[13px] text-muted">{team.status === 'live' ? 'They’re emailed an invite straight away.' : 'They’re emailed when you send the team’s invites.'}</p>
      {add.isError && !f.email && (
        <div className="mt-3">
          <Alert>{errorMessage(add.error)}</Alert>
        </div>
      )}
    </Section>
  );
}

// ---------- Players ----------

function Players({ clubId, team, onAdd }: { clubId: string; team: ClubTeamDetail; onAdd: () => void }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => api.del<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/players/${id}`),
    onSuccess: (t) => {
      qc.setQueryData(['club-team', clubId, team.id], t);
      void qc.invalidateQueries({ queryKey: ['club-players', clubId] });
    },
  });
  return (
    <Section
      title={`Players (${team.roster.length})`}
      aside={
        <Button variant="secondary" onClick={onAdd}>
          <Plus className="size-4" aria-hidden /> Add players
        </Button>
      }
    >
      {!team.roster.length ? (
        <p className="text-muted">No players yet. Add them from the club’s player list.</p>
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {team.roster.map((p) => (
            <li key={p.rosterPlayerId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {p.number && <span className="tnum mr-1.5 text-muted">#{p.number}</span>}
                  {p.name}
                </p>
                {p.contacts.length ? (
                  <p className="text-[13px] text-muted">
                    {p.contacts.map((x, i) => (
                      <span key={x.email + x.kind}>
                        {i > 0 && ', '}
                        {x.email}
                        {x.kind === 'parent' ? ' (parent)' : ''} <span className={CONTACT_STATUS[x.status]?.cls}>{CONTACT_STATUS[x.status]?.label}</span>
                      </span>
                    ))}
                  </p>
                ) : (
                  <p className="text-[13px] text-warn">No email: nobody will be invited for this player</p>
                )}
              </div>
              <button className="btn-ghost p-1.5" onClick={() => remove.mutate(p.rosterPlayerId)} aria-label={`Take ${p.name} off the team`} title="Take off the team (attendance history kept)">
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {remove.isError && (
        <div className="mt-3">
          <Alert>{errorMessage(remove.error)}</Alert>
        </div>
      )}
    </Section>
  );
}

function AddPlayersDialog({ clubId, team, onClose }: { clubId: string; team: ClubTeamDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const players = useQuery({ queryKey: ['club-players', clubId], queryFn: () => api.get<ClubPlayer[]>(`/clubs/${clubId}/players`) });
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const onTeam = new Set(team.roster.map((p) => p.clubPlayerId).filter(Boolean));
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (players.data ?? []).filter((p) => !onTeam.has(p.id) && (!s || p.name.toLowerCase().includes(s)));
  }, [players.data, q, team.roster]);
  const assign = useMutation({
    mutationFn: () => api.post<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/players`, { clubPlayerIds: picked }),
    onSuccess: (t) => {
      qc.setQueryData(['club-team', clubId, team.id], t);
      void qc.invalidateQueries({ queryKey: ['club-players', clubId] });
      onClose();
    },
  });
  if (adding) return <ClubPlayerDialog clubId={clubId} player={null} onClose={() => setAdding(false)} onSaved={(p) => setPicked((x) => [...x, p.id])} />;
  return (
    <Dialog open onClose={onClose} title={`Add players to ${team.name}`} size="lg">
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" aria-hidden />
            <input className="field-input pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the club’s players" aria-label="Search players" autoFocus />
          </div>
          <Button variant="secondary" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden /> New player
          </Button>
        </div>
        {players.isLoading ? (
          <LoadingBlock />
        ) : !shown.length ? (
          <p className="py-6 text-center text-muted">{players.data?.length ? 'Everyone matching is already on this team.' : 'The club’s player list is empty. Add a new player.'}</p>
        ) : (
          <ul className="max-h-[50vh] divide-y divide-line overflow-y-auto rounded-lg border border-line">
            {shown.map((p) => (
              <li key={p.id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-sunken">
                  <input type="checkbox" className="size-4 accent-[var(--action)]" checked={picked.includes(p.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, p.id] : picked.filter((x) => x !== p.id))} />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{p.name}</span>
                    <span className="block truncate text-[13px] text-muted">{[p.contacts.length ? `${p.contacts.length} ${p.contacts.length === 1 ? 'email' : 'emails'}` : 'No email', p.teams.length ? `on ${p.teams.map((t) => t.teamName).join(', ')}` : null].filter(Boolean).join(' · ')}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="text-[13px] text-muted">{team.status === 'live' ? 'Their emails are invited straight away.' : 'Their emails are invited when you send the team’s invites.'}</p>
        {assign.isError && <Alert>{errorMessage(assign.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={assign.isPending} disabled={!picked.length} onClick={() => assign.mutate()}>
            Add {picked.length || ''} {picked.length === 1 ? 'player' : 'players'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------- Team details ----------

function EditTeamDialog({ clubId, team, onClose }: { clubId: string; team: ClubTeamDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const seasons = useQuery({ queryKey: ['club-seasons', clubId], queryFn: () => api.get<ClubSeason[]>(`/clubs/${clubId}/seasons`) });
  const [v, setV] = useState({ name: team.name, ageGroup: team.ageGroup ?? '', clubSeasonId: team.clubSeasonId ?? '' });
  const save = useMutation({
    mutationFn: () => api.patch<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}`, { ...v, clubSeasonId: v.clubSeasonId || null }),
    onSuccess: (t) => {
      qc.setQueryData(['club-team', clubId, team.id], t);
      void qc.invalidateQueries({ queryKey: ['club-teams', clubId] });
      onClose();
    },
  });
  const f = fieldErrors(save.error);
  return (
    <Dialog open onClose={onClose} title="Edit team">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
        {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Team name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} error={f.name} autoFocus />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Age group" value={v.ageGroup} onChange={(e) => setV({ ...v, ageGroup: e.target.value })} error={f.ageGroup} />
          <div className="space-y-1.5">
            <label htmlFor="edit-season" className="label-caps block">
              Season
            </label>
            <select id="edit-season" className="field-input" value={v.clubSeasonId} onChange={(e) => setV({ ...v, clubSeasonId: e.target.value })}>
              <option value="">No season</option>
              {seasons.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RemoveTeamDialog({ clubId, team, onClose }: { clubId: string; team: ClubTeamDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const remove = useMutation({
    mutationFn: () => api.del(`/clubs/${clubId}/teams/${team.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['club-teams', clubId] });
      void qc.invalidateQueries({ queryKey: ['club', clubId] });
      navigate(`/clubs/${clubId}`);
    },
  });
  const live = team.status === 'live';
  return (
    <Dialog open onClose={onClose} title={live ? `Take ${team.name} out of the club?` : `Delete the draft ${team.name}?`}>
      <div className="space-y-4">
        <p className="text-[14px] text-muted">
          {live
            ? 'The team carries on with its coaches and players; the club just stops looking after it. Its PlayHQ link is removed, and its games become ordinary games the coaches can edit.'
            : 'Nobody has been emailed yet, so nobody will notice. Planned invites are cancelled. Players stay on the club’s list.'}
        </p>
        {remove.isError && <Alert>{errorMessage(remove.error)}</Alert>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" className="btn-danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            {live ? 'Take out of club' : 'Delete draft'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------- PlayHQ ----------

function PlayhqLink({ clubId, team }: { clubId: string; team: ClubTeamDetail }) {
  const qc = useQueryClient();
  const [choosing, setChoosing] = useState(false);
  const [season, setSeason] = useState('');
  const seasons = useQuery({ queryKey: ['phq-seasons', clubId], queryFn: () => api.get<PlayhqSeason[]>(`/clubs/${clubId}/playhq/seasons`), enabled: choosing });
  const teams = useQuery({ queryKey: ['phq-teams', clubId, season], queryFn: () => api.get<PlayhqTeam[]>(`/clubs/${clubId}/playhq/seasons/${season}/teams`), enabled: !!season });
  const link = useMutation({
    mutationFn: (p: PlayhqTeam | null) => api.put<ClubTeamDetail>(`/clubs/${clubId}/teams/${team.id}/playhq`, p ? { playhqTeamId: p.id, gradeId: p.gradeId, gradeName: p.gradeName } : { playhqTeamId: null }),
    onSuccess: (t) => {
      qc.setQueryData(['club-team', clubId, team.id], t);
      void qc.invalidateQueries({ queryKey: ['club-teams', clubId] });
      setChoosing(false);
    },
  });
  return (
    <Section title="PlayHQ" description="A linked team gets its fixture as games, kept up to date with times, venues and results, and its ladder.">
      {team.playhq && !choosing ? (
        <div className="space-y-3 text-[14px]">
          <p>
            Linked to <span className="font-medium">{team.playhq.gradeName ?? 'a PlayHQ team'}</span>
            <span className="text-muted">{team.playhq.syncedAt ? `, synced ${timeAgo(team.playhq.syncedAt)}` : ''}</span>
          </p>
          {team.playhq.error && <Alert tone="warn">{team.playhq.error}</Alert>}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setChoosing(true)}>
              Change
            </Button>
            <Button variant="ghost" loading={link.isPending} onClick={() => link.mutate(null)}>
              Unlink
            </Button>
          </div>
        </div>
      ) : !choosing ? (
        <Button variant="secondary" onClick={() => setChoosing(true)}>
          Link to a PlayHQ team
        </Button>
      ) : (
        <div className="max-w-[560px] space-y-3">
          {seasons.isLoading && <LoadingBlock />}
          {seasons.isError && <Alert>{errorMessage(seasons.error)}</Alert>}
          {seasons.data && (
            <select className="field-input" value={season} onChange={(e) => setSeason(e.target.value)} aria-label="PlayHQ season">
              <option value="">Choose a PlayHQ season</option>
              {seasons.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.name, s.competition].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          )}
          {teams.isLoading && <LoadingBlock />}
          {teams.data && (
            <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
              {teams.data.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{p.name}</span>
                    <span className="block text-[13px] text-muted">{p.gradeName}</span>
                  </span>
                  {p.linkedTeamId && p.linkedTeamId !== team.id ? (
                    <span className="text-[13px] text-muted">Linked to another team</span>
                  ) : (
                    <Button variant="secondary" loading={link.isPending && link.variables?.id === p.id} onClick={() => link.mutate(p)}>
                      {p.linkedTeamId === team.id ? 'Linked' : 'Link'}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {link.isError && <Alert>{errorMessage(link.error)}</Alert>}
          <Button variant="ghost" onClick={() => setChoosing(false)}>
            Cancel
          </Button>
        </div>
      )}
    </Section>
  );
}
